import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useZones,
  useCreateZone,
  useUpdateZone,
  useDeleteZone,
  useLocations,
  useCreateLocation,
  useUpdateLocation,
  useDeleteLocation,
  useLocationSummary,
} from '../hooks/useWmsLocations';
import { ZoneCard } from '../components/locations/ZoneCard';
import { BinGrid } from '../components/locations/BinCard';
import { ZoneFormModal } from '../components/locations/ZoneFormModal';
import { LocationFormModal } from '../components/locations/LocationFormModal';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';
import type { Zone, Location, CreateZoneInput, UpdateZoneInput, CreateLocationInput, UpdateLocationInput, LocationSummary, ZonesResponse, LocationsResponse } from '../types';

export default function LocationsPage() {
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [zoneModalOpen, setZoneModalOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [zoneToDelete, setZoneToDelete] = useState<Zone | null>(null);
  const [locationToDelete, setLocationToDelete] = useState<Location | null>(null);

  // Queries
  const zonesQuery = useZones();
  const summaryQuery = useLocationSummary();
  const locationsQuery = useLocations(
    selectedZone ? { zone_id: selectedZone.id, page_length: 100 } : { page_length: 100 }
  );

  // Mutations
  const createZoneMutation = useCreateZone();
  const updateZoneMutation = useUpdateZone();
  const deleteZoneMutation = useDeleteZone();
  const createLocationMutation = useCreateLocation();
  const updateLocationMutation = useUpdateLocation();
  const deleteLocationMutation = useDeleteLocation();

  const zonesData = zonesQuery.data as ZonesResponse | undefined;
  const summaryData = summaryQuery.data as LocationSummary | undefined;
  const locationsData = locationsQuery.data as LocationsResponse | undefined;
  const zonesLoading = zonesQuery.isLoading;
  const locationsLoading = locationsQuery.isLoading;

  const zones = zonesData?.zones || [];
  const locations = locationsData?.locations || [];
  const zoneSummary = summaryData?.zones || [];

  // Get summary stats for each zone
  const getZoneSummary = (zoneId: string) => {
    return zoneSummary.find((s) => s.zone_id === zoneId);
  };

  const handleCreateZone = async (data: CreateZoneInput | UpdateZoneInput) => {
    await createZoneMutation.mutateAsync(data as CreateZoneInput);
    setZoneModalOpen(false);
  };

  const handleUpdateZone = async (data: CreateZoneInput | UpdateZoneInput) => {
    if (editingZone) {
      await updateZoneMutation.mutateAsync({ id: editingZone.id, data: data as UpdateZoneInput });
      setEditingZone(null);
      setZoneModalOpen(false);
    }
  };

  const handleDeleteZone = async () => {
    if (!zoneToDelete) return;
    await deleteZoneMutation.mutateAsync(zoneToDelete.id);
    if (selectedZone?.id === zoneToDelete.id) {
      setSelectedZone(null);
    }
    setZoneToDelete(null);
  };

  const handleCreateLocation = async (data: CreateLocationInput | UpdateLocationInput) => {
    await createLocationMutation.mutateAsync(data as CreateLocationInput);
    setLocationModalOpen(false);
  };

  const handleUpdateLocation = async (data: CreateLocationInput | UpdateLocationInput) => {
    if (editingLocation) {
      await updateLocationMutation.mutateAsync({ id: editingLocation.id, data: data as UpdateLocationInput });
      setEditingLocation(null);
      setLocationModalOpen(false);
    }
  };

  const handleDeleteLocation = async () => {
    if (!locationToDelete) return;
    await deleteLocationMutation.mutateAsync(locationToDelete.id);
    setLocationToDelete(null);
    setEditingLocation(null);
    setLocationModalOpen(false);
  };

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            Locations <small>Manage workshop zones and bins</small>
          </h1>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setEditingZone(null);
              setZoneModalOpen(true);
            }}
          >
            Add Zone
          </Button>
          <Button
            onClick={() => {
              setEditingLocation(null);
              setLocationModalOpen(true);
            }}
            disabled={zones.length === 0}
          >
            Add Bin
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {summaryData && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Zones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summaryData.totals.total_zones}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Locations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summaryData.totals.total_locations}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Active Locations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summaryData.totals.active_locations}</div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="zones" className="w-full">
        <TabsList>
          <TabsTrigger value="zones">Zones</TabsTrigger>
          <TabsTrigger value="bins">All Bins</TabsTrigger>
        </TabsList>

        <TabsContent value="zones" className="space-y-4">
          {/* Zone Grid */}
          {zonesLoading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {[...Array(4)].map((_, i) => (
                <Card key={i} className="p-4">
                  <Skeleton className="h-5 w-1/2 mb-2" />
                  <Skeleton className="h-4 w-3/4 mb-4" />
                  <Skeleton className="h-4 w-full" />
                </Card>
              ))}
            </div>
          ) : zones.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground mb-4">
                  No zones configured. Start by creating your first workshop zone.
                </p>
                <Button
                  onClick={() => {
                    setEditingZone(null);
                    setZoneModalOpen(true);
                  }}
                >
                  Create First Zone
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {zones.map((zone) => {
                  const summary = getZoneSummary(zone.id);
                  return (
                    <ZoneCard
                      key={zone.id}
                      zone={zone}
                      locationCount={summary?.location_count || 0}
                      activeCount={summary?.active_count || 0}
                      isSelected={selectedZone?.id === zone.id}
                      onSelect={() => setSelectedZone(selectedZone?.id === zone.id ? null : zone)}
                      onEdit={() => {
                        setEditingZone(zone);
                        setZoneModalOpen(true);
                      }}
                    />
                  );
                })}
              </div>

              {/* Bins for selected zone */}
              {selectedZone && (
                <Card className="mt-6">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: selectedZone.color || '#6b7280' }}
                        />
                        <CardTitle>
                          {selectedZone.code} - {selectedZone.name}
                        </CardTitle>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setZoneToDelete(selectedZone)}
                        >
                          Delete Zone
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setEditingLocation(null);
                            setLocationModalOpen(true);
                          }}
                        >
                          Add Bin
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {locationsLoading ? (
                      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                        {[...Array(8)].map((_, i) => (
                          <Skeleton key={i} className="h-20" />
                        ))}
                      </div>
                    ) : (
                      <BinGrid
                        locations={locations}
                        onLocationClick={(location) => {
                          setEditingLocation(location);
                          setLocationModalOpen(true);
                        }}
                      />
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="bins" className="space-y-4">
          {/* All bins view */}
          {locationsLoading ? (
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {[...Array(12)].map((_, i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : locations.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">
                  No bin locations found. Create zones first, then add bins to them.
                </p>
              </CardContent>
            </Card>
          ) : (
            <BinGrid
              locations={locations}
              onLocationClick={(location) => {
                setEditingLocation(location);
                setLocationModalOpen(true);
              }}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <ZoneFormModal
        open={zoneModalOpen}
        onOpenChange={(open) => {
          setZoneModalOpen(open);
          if (!open) setEditingZone(null);
        }}
        zone={editingZone}
        onSubmit={editingZone ? handleUpdateZone : handleCreateZone}
        isLoading={createZoneMutation.isPending || updateZoneMutation.isPending}
      />

      <LocationFormModal
        open={locationModalOpen}
        onOpenChange={(open) => {
          setLocationModalOpen(open);
          if (!open) setEditingLocation(null);
        }}
        location={editingLocation}
        zones={zones}
        defaultZoneId={selectedZone?.id}
        onSubmit={editingLocation ? handleUpdateLocation : handleCreateLocation}
        onDelete={editingLocation ? () => setLocationToDelete(editingLocation) : undefined}
        isLoading={createLocationMutation.isPending || updateLocationMutation.isPending}
      />

      <ConfirmDialog
        open={!!zoneToDelete}
        onOpenChange={(open) => !open && setZoneToDelete(null)}
        title="Delete Zone"
        description={`Are you sure you want to delete zone "${zoneToDelete?.code}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteZone}
        isLoading={deleteZoneMutation.isPending}
      />

      <ConfirmDialog
        open={!!locationToDelete}
        onOpenChange={(open) => !open && setLocationToDelete(null)}
        title="Delete Bin"
        description={`Are you sure you want to delete bin "${locationToDelete?.full_code || `${locationToDelete?.zone_code}-${locationToDelete?.bin_code}`}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteLocation}
        isLoading={deleteLocationMutation.isPending}
      />
    </div>
  );
}
